import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { ordersAPI, workOrdersAPI, orderReviewsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { 
  FileDown, Trash2, Loader2, Package, ClipboardCheck,
  Factory, Edit2, Eye, Printer, Download, RefreshCw
} from 'lucide-react';
import { ProductionStatusTab } from '../components/orders/ProductionStatusTab';
import { CreateWorkOrderFromOrderDialog } from '../components/orders/CreateWorkOrderFromOrderDialog';
import { OrderDetailsCards } from '../components/orders/OrderDetailsCards';
import { OrderItemsTable } from '../components/orders/OrderItemsTable';
import { OrderTermsCard } from '../components/orders/OrderTermsCard';
import { EditOrderModal } from '../components/orders/EditOrderModal';
import { PartSyncDiffDialog } from '../components/shared/PartSyncDiffDialog';
import { PdfColumnSelector } from '../components/shared/PdfColumnSelector';
import { DEFAULT_PROFILES } from '../config/tableColumns';
import { ORDER_CSV_HEADERS, orderToCsvRows, rowsToCsv, downloadCsv } from '../utils/orderCsvExport';

export function OrderDetailPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [workOrders, setWorkOrders] = useState([]);
  const [reviewStatus, setReviewStatus] = useState(null);
  const [createWOOpen, setCreateWOOpen] = useState(false);
  const [loadingWOs, setLoadingWOs] = useState(false);
  const orderId = window.location.pathname.split('/')[2];

  // Terms & Conditions Edit State
  const [editingTerms, setEditingTerms] = useState(false);
  const [termsText, setTermsText] = useState('');
  const [savingTerms, setSavingTerms] = useState(false);

  // Edit/Amend Order State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [editForm, setEditForm] = useState({
    customer_po_number: '',
    customer_po_date: '',
    delivery_date: '',
    notes: '',
    ship_to_address: '',
  });
  const [editItems, setEditItems] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);

  // Production schedule state
  const [viewStartDate, setViewStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const daysToShow = 28;

  const dateColumns = Array.from({ length: daysToShow }, (_, i) => {
    const d = new Date(viewStartDate);
    d.setDate(d.getDate() + i);
    return d;
  });

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await ordersAPI.get(orderId);
        setOrder(res.data);
        setTermsText(res.data.terms_conditions || '');
      } catch (error) {
        toast.error('Failed to load order');
        navigate('/orders');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId, navigate]);

  useEffect(() => {
    if (orderId) {
      orderReviewsAPI.getForOrder(orderId)
        .then((res) => setReviewStatus(res.data))
        .catch(() => {});
    }
  }, [orderId]);

  const loadWorkOrders = React.useCallback(() => {
    if (!orderId) return;
    setLoadingWOs(true);
    workOrdersAPI.getByOrder(orderId)
      .then(res => setWorkOrders(res.data?.work_orders || []))
      .catch(() => toast.error('Failed to load work orders'))
      .finally(() => setLoadingWOs(false));
  }, [orderId]);

  useEffect(() => {
    if (activeTab === 'production') loadWorkOrders();
  }, [activeTab, loadWorkOrders]);

  const formatCurrency = (amount, currency = 'INR') => {
    const symbol = currency === 'INR' ? '\u20B9' : currency === 'USD' ? '$' : '\u20AC';
    return `${symbol}${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await ordersAPI.delete(orderId);
      toast.success('Order deleted');
      navigate('/orders');
    } catch (error) {
      toast.error('Failed to delete order');
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveTerms = async () => {
    setSavingTerms(true);
    try {
      await ordersAPI.update(orderId, { terms_conditions: termsText });
      setOrder(prev => ({ ...prev, terms_conditions: termsText }));
      setEditingTerms(false);
      toast.success('Terms & conditions updated');
    } catch (error) {
      toast.error('Failed to save terms');
    } finally {
      setSavingTerms(false);
    }
  };

  const openEditModal = () => {
    setEditForm({
      customer_po_number: order.customer_po_number || '',
      customer_po_date: order.customer_po_date ? order.customer_po_date.split('T')[0] : '',
      delivery_date: order.delivery_date ? order.delivery_date.split('T')[0] : '',
      notes: order.notes || '',
      ship_to_address: order.ship_to_address || '',
    });
    setEditItems((order.items || []).map(item => ({
      id: item.id,
      part_internal_no: item.part_internal_no || '',
      part_description: item.description || '',
      quantity: item.quantity || 0,
      unit_price: item.unit_price || 0,
      customer_part_no: item.customer_part_no || '',
      hs_code: item.hs_code || '',
      net_weight_grams: item.net_weight_grams ?? '',
      uom: item.uom || 'pcs',
      packing_qty: item.packing_qty ?? '',
      box_size: item.box_size || '',
      price_unit: item.price_unit || 1,
      grade: item.grade || '',
      finish: item.finish || '',
      manufacturing_lead_time: item.manufacturing_lead_time || '',
      sample_lead_time: item.sample_lead_time || '',
      shipment_date: item.shipment_date ? item.shipment_date.split('T')[0] : '',
      tooling_cost: item.tooling_cost ?? 0,
      sample_cost: item.sample_cost ?? 0,
      remarks: item.remarks || '',
    })));
    setEditModalOpen(true);
  };

  const updateEditItem = (idx, field, value) => {
    setEditItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      const updateData = {
        customer_po_number: editForm.customer_po_number,
        customer_po_date: editForm.customer_po_date || null,
        delivery_date: editForm.delivery_date || null,
        notes: editForm.notes,
        items: editItems.map(item => ({
          id: item.id,
          part_internal_no: item.part_internal_no,
          description: item.part_description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          customer_part_no: item.customer_part_no,
          hs_code: item.hs_code,
          net_weight_grams: item.net_weight_grams === '' ? null : parseFloat(item.net_weight_grams) || 0,
          uom: item.uom,
          packing_qty: item.packing_qty === '' ? null : parseFloat(item.packing_qty) || 0,
          box_size: item.box_size,
          price_unit: parseFloat(item.price_unit) || 1,
          grade: item.grade,
          finish: item.finish,
          manufacturing_lead_time: item.manufacturing_lead_time,
          sample_lead_time: item.sample_lead_time,
          shipment_date: item.shipment_date,
          tooling_cost: parseFloat(item.tooling_cost) || 0,
          sample_cost: parseFloat(item.sample_cost) || 0,
          remarks: item.remarks,
        }))
      };
      await ordersAPI.update(orderId, updateData);
      const res = await ordersAPI.get(orderId);
      setOrder(res.data);
      setEditModalOpen(false);
      toast.success('Sales Confirmation amended successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save amendment');
    } finally {
      setSavingEdit(false);
    }
  };

  const exportOrderToCSV = () => {
    if (!order) return;
    const rows = orderToCsvRows(order);
    const csvContent = rowsToCsv(ORDER_CSV_HEADERS, rows);
    downloadCsv(csvContent, `${order.order_number}_items.csv`);
    toast.success('Order items exported to CSV');
  };

  const downloadPdf = () => {
    setShowPrintPreview(true);
  };

  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [syncingParts, setSyncingParts] = useState(false);
  const [syncDiff, setSyncDiff] = useState(null);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [applyingSync, setApplyingSync] = useState(false);

  const handleRefreshFromPartMaster = async () => {
    setSyncingParts(true);
    try {
      const res = await ordersAPI.syncParts(orderId, true);  // dry-run
      const updated = res.data?.updated_count ?? 0;
      if (updated === 0) {
        toast.info('No changes detected — order is already in sync with Part Master.');
        return;
      }
      setSyncDiff(res.data);
      setSyncDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview Part Master sync');
    } finally {
      setSyncingParts(false);
    }
  };

  const handleConfirmPartSync = async () => {
    setApplyingSync(true);
    try {
      const res = await ordersAPI.syncParts(orderId, false);
      toast.success(`Refreshed ${res.data?.updated_count ?? 0} line item(s) from Part Master`);
      setSyncDialogOpen(false);
      setSyncDiff(null);
      const fresh = await ordersAPI.get(orderId);
      setOrder(fresh.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply Part Master refresh');
    } finally {
      setApplyingSync(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setPdfDownloading(true);
      const { downloadPdfFromIframe } = await import('./print-views/shared');
      const filename = `SalesOrder_${order.order_number}.pdf`;
      await downloadPdfFromIframe(
        '[data-testid="order-preview-iframe"]',
        'landscape',
        filename,
        null,
        (success) => {
          setPdfDownloading(false);
          if (success) toast.success('PDF downloaded');
          else toast.error('PDF generation failed');
        }
      );
    } catch {
      setPdfDownloading(false);
      toast.error('Failed to generate PDF. Please try Print instead.');
    }
  };

  const downloadSchedulePDF = async () => {
    toast.info('Schedule PDF generation coming soon');
  };

  const navigateWeek = (direction) => {
    setViewStartDate(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + (direction * 7));
      return d;
    });
  };

  const getWOBarStyle = (wo) => {
    const status = wo.status || 'pending';
    const colors = {
      pending: 'bg-yellow-400', in_progress: 'bg-blue-500',
      completed: 'bg-green-500', cancelled: 'bg-gray-400'
    };
    return colors[status] || 'bg-gray-400';
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!order) return null;

  const isAdmin = user?.role === 'admin';
  const previewUrl = `/orders/${orderId}/print?preview=true`;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Sales Order PDF Preview Modal */}
      <Dialog open={showPrintPreview} onOpenChange={setShowPrintPreview}>
        <DialogContent className="max-w-[900px] w-[95vw] h-[90vh] p-0 flex flex-col" data-testid="order-preview-modal">
          <DialogHeader className="px-6 pt-4 pb-2 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-primary" />
              Sales Order Preview
            </DialogTitle>
            <DialogDescription>
              {order.order_number} — {order.customer_name}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-hidden">
            <iframe
              src={previewUrl}
              title="Order Print Preview"
              className="w-full h-full border-0"
              data-testid="order-preview-iframe"
            />
          </div>
          <DialogFooter className="px-6 py-3 border-t shrink-0 flex flex-row items-center justify-end gap-2">
            <Button variant="outline" onClick={() => setShowPrintPreview(false)}>Close</Button>
            <Button variant="outline" onClick={() => {
              const iframe = document.querySelector('[data-testid="order-preview-iframe"]');
              if (iframe?.contentWindow) iframe.contentWindow.print();
            }}>
              <Printer className="mr-2 h-4 w-4" /> Print
            </Button>
            <Button onClick={handleDownloadPdf} disabled={pdfDownloading} data-testid="order-download-pdf-btn">
              {pdfDownloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              {pdfDownloading ? 'Generating...' : 'Download PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-tight">{order.order_number}</h1>
          <p className="text-muted-foreground">{order.customer_name} &bull; {order.customer_code}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate('/orders')}>Back</Button>
          <Button variant="outline" onClick={openEditModal}
            className="border-blue-200 text-blue-600 hover:bg-blue-50" data-testid="edit-order-btn">
            <Edit2 className="mr-2 h-4 w-4" /> Edit / Amend
          </Button>
          <Button variant="outline" onClick={exportOrderToCSV} data-testid="export-single-order-csv-btn">
            <FileDown className="mr-2 h-4 w-4" /> Export to CSV
          </Button>
          <Button variant="outline" onClick={handleRefreshFromPartMaster}
            disabled={syncingParts}
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            data-testid="refresh-from-part-master-btn"
            title="Pull latest description / part number / HS code etc. from Part Master into this order">
            {syncingParts ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh from Part Master
          </Button>
          <Button variant="outline" onClick={() => navigate(`/orders/${orderId}/review-sheet`)}
            className={reviewStatus?.status === 'signed_uploaded'
              ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
              : 'border-amber-300 text-amber-700 hover:bg-amber-50'}
            data-testid="order-review-sheet-btn"
            title="Order Review & Feasibility Sheet (F-MKT-04)">
            <ClipboardCheck className="mr-2 h-4 w-4" />
            {reviewStatus?.status === 'signed_uploaded' ? 'Review sheet signed'
              : reviewStatus?.status === 'downloaded' ? 'Review sheet — awaiting signed copy'
                : reviewStatus?.exists ? 'Review sheet — in progress' : 'Review sheet pending'}
          </Button>
          <Button variant="outline" onClick={downloadPdf}>
            <Download className="mr-2 h-4 w-4" /> Download PDF
          </Button>
          
          {isAdmin && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  disabled={deleting} data-testid="delete-order-btn">
                  {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Order</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete <strong>{order.order_number}</strong>?
                    <br /><br />This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="details" className="flex items-center gap-2">
            <Package className="h-4 w-4" /> Order Details
          </TabsTrigger>
          <TabsTrigger value="production" className="flex items-center gap-2">
            <Factory className="h-4 w-4" /> Production Status
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6">
          <OrderDetailsCards order={order} formatDate={formatDate} />
          {/* PDF Table Columns Selector */}
          <PdfColumnSelector
            selectedColumns={order.pdf_columns || DEFAULT_PROFILES.order}
            autoHideEmpty={order.auto_hide_empty !== false}
            docType="order"
            onChange={async (cols, autoHide) => {
              try {
                await ordersAPI.update(orderId, { pdf_columns: cols, auto_hide_empty: autoHide });
                setOrder(prev => ({ ...prev, pdf_columns: cols, auto_hide_empty: autoHide }));
                toast.success('PDF columns updated');
              } catch { toast.error('Failed to update columns'); }
            }}
          />
          <OrderItemsTable order={order} formatCurrency={formatCurrency} />
          <OrderTermsCard
            order={order}
            editingTerms={editingTerms}
            setEditingTerms={setEditingTerms}
            termsText={termsText}
            setTermsText={setTermsText}
            onSaveTerms={handleSaveTerms}
            savingTerms={savingTerms}
          />
        </TabsContent>

        <TabsContent value="production" className="space-y-4">
          <ProductionStatusTab
            workOrders={workOrders}
            loadingWOs={loadingWOs}
            dateColumns={dateColumns}
            viewStartDate={viewStartDate}
            daysToShow={daysToShow}
            formatDate={formatDate}
            getWOBarStyle={getWOBarStyle}
            onNavigateWeek={navigateWeek}
            onSetViewStartDate={setViewStartDate}
            onDownloadSchedulePDF={downloadSchedulePDF}
            onCreateWorkOrder={() => setCreateWOOpen(true)}
          />
          <CreateWorkOrderFromOrderDialog
            open={createWOOpen}
            onOpenChange={setCreateWOOpen}
            order={order}
            workOrders={workOrders}
            onCreated={loadWorkOrders}
          />
        </TabsContent>
      </Tabs>

      <EditOrderModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        order={order}
        editForm={editForm}
        setEditForm={setEditForm}
        editItems={editItems}
        updateEditItem={updateEditItem}
        onSave={handleSaveEdit}
        saving={savingEdit}
        formatCurrency={formatCurrency}
      />

      <PartSyncDiffDialog
        open={syncDialogOpen}
        onOpenChange={(v) => { setSyncDialogOpen(v); if (!v) setSyncDiff(null); }}
        changes={syncDiff?.changes || []}
        onConfirm={handleConfirmPartSync}
        applying={applyingSync}
        docLabel="sales order"
      />
    </div>
  );
}
